import type {
  CardinalityDescriptor,
  DocumentTypeDescriptor,
  DocumentTypeRef,
  QueryFieldDescriptor,
  QueryFieldGroupDescriptor,
  QueryEditorDescriptor,
  QueryRelationDescriptor,
  QueryValueKind,
  QueryValueOption,
  ReferenceSelectionSource,
} from '@aeppic/query/contracts'

import { parse, Scopes } from '../../model/query/index.js'

import type { FieldInfo, Form } from '../../model/form.js'
import { DOCUMENT_METADATA_FIELDS, operatorsForValueKind } from './document-metadata.js'
import type { QueryToolbarAeppicApi } from './types.js'

export async function describeDocumentTypes(aeppic: QueryToolbarAeppicApi, types: readonly DocumentTypeRef[]): Promise<readonly DocumentTypeDescriptor[]> {
  const cached = cachedDefinitionApi(aeppic)
  return Promise.all(types.map(async type => describeDocumentType(cached, type)))
}

async function describeDocumentType(aeppic: QueryToolbarAeppicApi, type: DocumentTypeRef): Promise<DocumentTypeDescriptor> {
  const form = await aeppic.getForm(type.version ? { id: type.id, v: type.version } : type.id)

  if (!form) {
    throw new Error(`Form ${type.id}${type.version ? `@${type.version}` : ''} not found`)
  }

  const fieldGroups = fieldGroupsForForm(form)
  const fieldsPromise = Promise.all(form.fields.map(async field => {
    const descriptor = describeField(form, field, fieldGroups.get(field.name))
    await describeSelectionSource(aeppic, form, field, descriptor)
    return descriptor
  }))
  const directRelations = form.fields
    .filter(field => field.type === 'object' && field.subType === 'ref')
    .map(field => describeReferenceRelation(form, field))
  const [fields, namedRelations] = await Promise.all([fieldsPromise, describeNamedRelations(aeppic, form)])
  const name = form.name || form.id
  const label = readNonEmptyString(form.document.data.label) || name

  return {
    type: { id: form.id, version: form.v },
    name,
    label,
    fields: [...fields, ...DOCUMENT_METADATA_FIELDS],
    relations: [...directRelations, ...namedRelations],
  }
}

function describeField(form: Form, field: FieldInfo, group?: QueryFieldGroupDescriptor): QueryFieldDescriptor {
  const valueKind = valueKindForField(field)
  const referenceTargets = referenceTargetsForField(form, field)
  const path = valueKind === 'reference'
    ? `data.${field.name}.id`
    : field.type === 'object' && field.subType === 'currency'
      ? `data.${field.name}.amount`
      : `data.${field.name}`
  const descriptor: QueryFieldDescriptor = {
    path,
    label: form.getFieldLabel(field.name) || field.name,
    valueKinds: [valueKind],
    editor: editorForField(field),
    cardinality: cardinalityForField(field),
    operators: operatorsForValueKind(valueKind),
    required: field.required === true || field.tags?.required === true,
    metadata: false,
  }

  if (group) {
    descriptor.group = group
  }

  if (valueKind === 'boolean' && typeof field.defaultValue === 'boolean') {
    descriptor.missingValue = field.defaultValue
  }
  const options = optionsForField(field)

  if (options.length > 0) {
    descriptor.options = options
  }

  if (valueKind === 'reference') {
    descriptor.referenceTargets = referenceTargets
    descriptor.referenceSelection = isRelationOnlyReference(form, field)
      ? 'relation-only'
      : 'direct-if-small'
  }

  return descriptor
}

function editorForField(field: FieldInfo): QueryEditorDescriptor {
  if (field.subType === 'select') return { kind: 'select' }
  if (field.type === 'string') {
    const temporalKinds: Record<string, QueryEditorDescriptor['kind']> = {
      date: 'date',
      datetime: 'datetime',
      time: 'time',
      dayofyear: 'day-of-year',
      monthofyear: 'month-of-year',
    }
    return { kind: temporalKinds[field.subType] ?? 'text' }
  }
  if (field.type === 'number') return { kind: 'number' }
  if (field.type === 'boolean') return { kind: 'boolean' }
  if (field.type === 'object' && field.subType === 'ref') return { kind: 'reference' }
  if (field.type === 'object' && field.subType === 'currency') {
    const currencyValue = field.defaultValue as { currency?: unknown, precision?: unknown } | undefined
    return {
      kind: 'currency',
      ...(typeof currencyValue?.currency === 'string' && currencyValue.currency ? { currency: currencyValue.currency } : {}),
      ...(typeof currencyValue?.precision === 'number' ? { precision: currencyValue.precision } : {}),
    }
  }
  if (field.type === 'object' && field.subType === 'duration') return { kind: 'duration' }
  return { kind: 'text' }
}

function fieldGroupsForForm(form: Form): Map<string, QueryFieldGroupDescriptor> {
  const groups = new Map<string, QueryFieldGroupDescriptor>()

  for (const section of form.info.sections) {
    const group: QueryFieldGroupDescriptor = section.index === 0
      ? { id: 'form:root', label: 'General' }
      : {
          id: `form:section:${section.index}`,
          label: readNonEmptyString(section.title) || `Section ${section.index}`,
        }

    for (const placeholder of section.placeholders ?? []) {
      const fieldName = placeholder.field?.name

      if (fieldName && !groups.has(fieldName)) {
        groups.set(fieldName, group)
      }
    }
  }

  return groups
}

function describeReferenceRelation(form: Form, field: FieldInfo): QueryRelationDescriptor {
  return {
    id: `field:${field.name}`,
    label: form.getFieldLabel(field.name) || field.name,
    kind: 'reference',
    cardinality: cardinalityForField(field),
    targetTypes: referenceTargetsForField(form, field),
    fieldPath: `data.${field.name}`,
    resolvePath: `data.${field.name}`,
  }
}

async function describeNamedRelations(aeppic: QueryToolbarAeppicApi, form: Form): Promise<QueryRelationDescriptor[]> {
  const relationDocuments = (await aeppic.getAll(form.getRelationRefs())).filter(Boolean)

  return Promise.all(relationDocuments.map(async relation => ({
    id: relation.id,
    label: readNonEmptyString(relation.data.label) || readNonEmptyString(relation.data.name) || relation.id,
    kind: 'named-relation' as const,
    cardinality: { min: 0, max: relation.data.multiple === true ? null : 1 },
    targetTypes: await targetTypesForNamedRelation(aeppic, relation, form),
    resolvePath: readNonEmptyString(relation.data.name),
  })))
}

async function targetTypesForNamedRelation(aeppic: QueryToolbarAeppicApi, relation, form: Form, visited = new Set<string>()): Promise<DocumentTypeRef[]> {
  if (visited.has(relation.id) || visited.size >= 32) {
    return []
  }
  const nextVisited = new Set(visited).add(relation.id)
  if (relation.data.type === 'RESOLVE') {
    return (await describeResolveSelection(aeppic, form, String(relation.data.resolve || ''), nextVisited)).targetTypes
  }
  if (relation.data.type === 'ANCESTOR_FORM' && Array.isArray(relation.data.ancestorForms)) {
    return relation.data.ancestorForms
      .filter(reference => reference?.id)
      .map(reference => ({ id: reference.id, ...(reference.v ? { version: reference.v } : {}) }))
  }

  if (relation.data.type === 'STATIC' && relation.data.reference?.id) {
    const target = await aeppic.get(relation.data.reference)
    return target?.f?.id ? [{ id: target.f.id, ...(target.f.v ? { version: target.f.v } : {}) }] : []
  }

  return []
}

function referenceTargetsForField(form: Form, field: FieldInfo): DocumentTypeRef[] {
  if (form.isRelationSource(field) || field.type !== 'object' || field.subType !== 'ref') {
    return []
  }

  const formRef = (field as FieldInfo & { formRef?: string }).formRef
  return formRef && formRef !== 'ref' ? [{ id: formRef }] : []
}

function isRelationOnlyReference(form: Form, field: FieldInfo): boolean {
  if (form.isRelationSource(field)) {
    return true
  }

  const control = form.getPlaceholders()
    .find(placeholder => placeholder.field?.name === field.name)
    ?.control
  const controlName = String(control?.fullName || control?.name || '').toLowerCase()

  return /(^|:)(child|children|mails)$/.test(controlName)
}

function cardinalityForField(field: FieldInfo): CardinalityDescriptor {
  if (!field.cardinality) {
    return { min: 0, max: 1 }
  }

  return {
    min: parseCardinalityNumber(field.cardinality.min, 0),
    max: field.cardinality.max == null || (field.cardinality.max as unknown) === 'n'
      ? null
      : parseCardinalityNumber(field.cardinality.max, null),
  }
}

function parseCardinalityNumber(value: unknown, fallback: number | null): number | null {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value), 10)
  return Number.isFinite(parsed) ? parsed : fallback
}

function optionsForField(field: FieldInfo): QueryValueOption[] {
  if (field.subType !== 'select' || !Array.isArray(field.settings?.options)) {
    return []
  }

  return field.settings.options
    .filter(option => option && isQueryPrimitive(option.value))
    .map(option => ({
      value: option.value,
      label: String(option.display ?? option.value ?? ''),
      ...helpDescriptor('help', option.help),
      ...helpDescriptor('helpExtended', option.helpExtended),
    }))
}

function helpDescriptor(key: 'help' | 'helpExtended', help: unknown): Partial<QueryValueOption> {
  if (!help || typeof help !== 'object') return {}
  const value = help as { text?: unknown, html?: unknown }
  if (typeof value.text !== 'string' || !value.text.trim()) return {}
  return {
    [key]: {
      text: value.text,
      ...(typeof value.html === 'string' && value.html ? { html: value.html } : {}),
    },
  }
}

function valueKindForField(field: FieldInfo): QueryValueKind {
  if (field.subType === 'select') {
    return 'select'
  }

  if (field.type === 'string') {
    return ['date', 'datetime', 'time', 'dayofyear', 'monthofyear'].includes(field.subType) ? 'datetime' : 'text'
  }

  if (field.type === 'number') {
    return 'number'
  }

  if (field.type === 'object' && field.subType === 'currency') {
    return 'number'
  }

  if (field.type === 'boolean') {
    return 'boolean'
  }

  if (field.type === 'object' && field.subType === 'ref') {
    return 'reference'
  }

  return 'unknown'
}

function isQueryPrimitive(value: unknown): value is string | number | boolean | null {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value)
}

function readNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

async function describeSelectionSource(aeppic: QueryToolbarAeppicApi, form: Form, field: FieldInfo, descriptor: QueryFieldDescriptor): Promise<void> {
  await resolveSelectionDefinition(aeppic, form, field, descriptor)
  if (!descriptor.referenceSources) {
    return
  }

  const control = form.getFieldControlParams(field.name) || {}
  const restrictions = []
  if (control.parentId) restrictions.push(Scopes.children(String(control.parentId)).toQuery())
  if (control.ancestorId) restrictions.push(Scopes.descendants(String(control.ancestorId)).toQuery())
  if (control.fixedControlFilter) restrictions.push(parse(String(control.fixedControlFilter)))
  if (form.isRelationSource(field) && Array.isArray(field.filter)) {
    // The parser's first f.id entry names the relation, rather than a target form.
    const extraFilters = field.filter.slice(1).map(filter => Object.entries(filter).map(([key, value]) => `${key}:${value}`).join(' AND ')).join(' AND ')
    if (extraFilters) restrictions.push(parse(extraFilters))
  }
  if (restrictions.length === 0) {
    return
  }
  descriptor.referenceSources = descriptor.referenceSources.map(source => {
    const builder = Scopes.filter().and(source.query)
    for (const restriction of restrictions) builder.and(restriction)
    return { ...source, query: builder.toQuery() }
  })
}

async function resolveSelectionDefinition(aeppic: QueryToolbarAeppicApi, form: Form, field: FieldInfo, descriptor: QueryFieldDescriptor): Promise<void> {
  if (field.type !== 'object' || field.subType !== 'ref') {
    return
  }
  const control = form.getFieldControlParams(field.name) || {}
  const contextualParameters = ['ancestorForm', 'ancestorIndex', 'ancestorSearchLimit', 'queryLimitCommand']
  if (contextualParameters.some(parameter => control[parameter] != null && control[parameter] !== '')) {
    descriptor.referenceSourceStatus = 'requires-document'
    descriptor.referenceSelection = 'relation-only'
    return
  }
  if (!form.isRelationSource(field)) {
    const targetTypes = descriptor.referenceTargets || []
    if (targetTypes.length > 0 && field.joinedFilter) {
      descriptor.referenceSources = [{ targetTypes, query: parse(field.joinedFilter) }]
      descriptor.referenceSourceStatus = 'resolved'
    }
    return
  }

  descriptor.referenceSourceStatus = 'requires-document'
  const relation = await form.findRelationByName(field.referenceSource!.name, aeppic)
  if (relation?.data.type === 'RESOLVE') {
    const selection = await describeResolveSelection(aeppic, form, String(relation.data.resolve || ''), new Set([relation.id]))
    if (selection.sources.length > 0) {
      descriptor.referenceTargets = selection.targetTypes
      descriptor.referenceSources = selection.sources
      descriptor.referenceSourceStatus = 'resolved'
      descriptor.referenceSelection = 'direct-if-small'
    }
    return
  }
  const reference = relation?.data.reference as { id?: string } | undefined
  if (relation?.data.type !== 'STATIC' || !reference?.id) {
    return
  }

  // Named static sources expose selectable children of a shared container.
  const container = await aeppic.get(reference.id)
  if (!container?.f?.id) {
    return
  }
  const containerForm = await aeppic.getForm(container.f)
  const childForms = containerForm?.document.data.allowedChildrenForms
  if (!Array.isArray(childForms) || childForms.length === 0) {
    return
  }

  const targetTypes = childForms.filter(reference => reference?.id).map(reference => ({ id: reference.id }))
  if (targetTypes.length === 0) {
    return
  }

  descriptor.referenceTargets = targetTypes
  descriptor.referenceSources = [{ targetTypes, query: Scopes.children(container.id).forms(targetTypes.map(type => type.id)).toQuery() }]
  descriptor.referenceSourceStatus = 'resolved'
  descriptor.referenceSelection = 'direct-if-small'
}

/** Follow definition edges only; source documents and assigned references are never read. */
async function describeResolveSelection(
  aeppic: QueryToolbarAeppicApi,
  startForm: Form,
  path: string,
  visited: Set<string>,
): Promise<{ targetTypes: DocumentTypeRef[], sources: ReferenceSelectionSource[] }> {
  let forms = [startForm]
  let targetTypes: DocumentTypeRef[] = []
  let sources: ReferenceSelectionSource[] = []
  const steps = path.split('->').map(step => step.trim()).filter(Boolean)

  for (const [index, step] of steps.entries()) {
    targetTypes = []
    sources = []
    for (const form of forms) {
      if (/^[A-Z]/.test(step)) {
        const relation = await form.findRelationByName(step, aeppic)
        if (!relation || visited.has(relation.id) || visited.size >= 32) {
          return { targetTypes: [], sources: [] }
        }
        if (relation.data.type === 'RESOLVE') {
          const nested = await describeResolveSelection(aeppic, form, String(relation.data.resolve || ''), new Set(visited).add(relation.id))
          targetTypes.push(...nested.targetTypes)
          sources.push(...nested.sources)
        } else {
          targetTypes.push(...await targetTypesForNamedRelation(aeppic, relation, form, visited))
        }
      } else {
        const match = /^data\.([^.]+)$/.exec(step)
        const field = match ? form.fields.find(candidate => candidate.name === match[1]) : null
        if (!field || field.type !== 'object' || field.subType !== 'ref' || form.isRelationSource(field)) {
          return { targetTypes: [], sources: [] }
        }
        const descriptor = describeField(form, field)
        await describeSelectionSource(aeppic, form, field, descriptor)
        if (descriptor.referenceSourceStatus === 'requires-document') {
          return { targetTypes: [], sources: [] }
        }
        targetTypes.push(...(descriptor.referenceTargets || []))
        sources.push(...(descriptor.referenceSources || []))
      }
    }
    if (targetTypes.length === 0) return { targetTypes: [], sources: [] }
    if (index === steps.length - 1 && sources.length < forms.length) {
      return { targetTypes, sources: [] }
    }
    if (index < steps.length - 1) {
      const nextForms = await Promise.all(targetTypes.map(type => aeppic.getForm(type.version ? { id: type.id, v: type.version } : type.id)))
      if (nextForms.some(form => !form)) return { targetTypes: [], sources: [] }
      forms = nextForms
    }
  }
  return { targetTypes, sources }
}

function cachedDefinitionApi(aeppic: QueryToolbarAeppicApi): QueryToolbarAeppicApi {
  const forms = new Map<string, ReturnType<QueryToolbarAeppicApi['getForm']>>()
  const documents = new Map<string, ReturnType<QueryToolbarAeppicApi['get']>>()
  const keyFor = (identifier: import('@aeppic/types').IdOrReference) => typeof identifier === 'string' ? identifier : identifier.id + '@' + (identifier.v || '')
  const get: QueryToolbarAeppicApi['get'] = identifier => {
    const key = keyFor(identifier)
    if (!documents.has(key)) documents.set(key, aeppic.get(identifier))
    return documents.get(key)!
  }
  return {
    get,
    getAll: identifiers => Promise.all(identifiers.map(get)),
    getForm: identifier => {
      const key = keyFor(identifier)
      if (!forms.has(key)) forms.set(key, aeppic.getForm(identifier))
      return forms.get(key)!
    },
    find: (query, options) => aeppic.find(query, options),
    resolveAll: (start, path) => aeppic.resolveAll(start, path),
  }
}
